import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';

/**
 * Searchable Select Component
 * A reusable dropdown component with search functionality
 *
 * @param {Object} props
 * @param {string|number} props.value - Currently selected value (ID)
 * @param {Function} props.onChange - Callback when selection changes
 * @param {Array} props.options - Array of options to display
 * @param {Function} props.getOptionLabel - Function to get display label from option
 * @param {Function} props.getOptionValue - Function to get value from option
 * @param {Function} props.filterOption - Optional custom filter function
 * @param {string} props.placeholder - Placeholder text for button
 * @param {string} props.searchPlaceholder - Placeholder text for search input
 * @param {string} props.noOptionsMessage - Message when no options found
 * @param {boolean} props.allowEmpty - Whether to show "No selection" option
 * @param {string} props.emptyLabel - Label for empty option
 * @param {string} props.className - Additional classes for the container
 */
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  getOptionLabel,
  getOptionValue,
  filterOption,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  noOptionsMessage = 'No options found',
  allowEmpty = true,
  emptyLabel = 'None',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Default filter function
  const defaultFilterOption = (option, search) => {
    const label = getOptionLabel(option).toLowerCase();
    return label.includes(search.toLowerCase());
  };

  const filteredOptions = options.filter(option => {
    if (!searchTerm.trim()) return true;
    return filterOption
      ? filterOption(option, searchTerm)
      : defaultFilterOption(option, searchTerm);
  });

  const selectedOption = options.find(opt => getOptionValue(opt) === value);

  const calculateDropdownPosition = () => {
    if (!dropdownRef.current) return {};

    const rect = dropdownRef.current.getBoundingClientRect();
    const dropdownWidth = 500; // Approximate width
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = rect.bottom;
    let left = rect.left;

    // Adjust horizontal position if it would go off-screen
    if (left + dropdownWidth > viewportWidth) {
      left = Math.max(10, viewportWidth - dropdownWidth - 10);
    }

    // Adjust vertical position if it would go off-screen
    if (top + 300 > viewportHeight) {
      top = Math.max(10, rect.top - 300);
    }

    return { top, left };
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (!isOpen) {
      setDropdownStyle(calculateDropdownPosition());
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="w-full text-left text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white flex items-center justify-between"
      >
        <span className="overflow-hidden text-ellipsis" title={selectedOption ? getOptionLabel(selectedOption) : placeholder}>
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <Search className="w-4 h-4 ml-2 flex-shrink-0 text-gray-400" />
      </button>

      {isOpen && (
        <div
          className="fixed z-[100] w-[500px] bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-hidden"
          style={{
            top: `${dropdownStyle.top}px`,
            left: `${dropdownStyle.left}px`
          }}
        >
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 pr-8"
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto">
            {allowEmpty && (
              <button
                onClick={() => handleSelect('')}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 border-b border-gray-100"
              >
                <span className="text-gray-500 italic">{emptyLabel}</span>
              </button>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 italic">
                {noOptionsMessage}
              </div>
            ) : (
              filteredOptions.map(option => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                return (
                  <button
                    key={optionValue}
                    onClick={() => handleSelect(optionValue)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 border-b border-gray-50 ${
                      value === optionValue ? 'bg-primary-50 text-primary-700' : ''
                    }`}
                    title={optionLabel}
                  >
                    <div className="break-words whitespace-normal">{optionLabel}</div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
